import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';
import { Drop, Plant, Sparkle, Star } from '@phosphor-icons/react';

const KEY_LOGO_URL = '/elemantra-key-logo.png';

export const BenefitsSection = () => {
  const { t } = useLanguage();
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.12,
        delayChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] },
    },
  };

  const benefits = [
    {
      icon: Drop,
      title: t.benefits.item1.title,
      desc: t.benefits.item1.desc,
    },
    {
      icon: Plant,
      title: t.benefits.item2.title,
      desc: t.benefits.item2.desc,
    },
    {
      icon: Sparkle,
      title: t.benefits.item3.title,
      desc: t.benefits.item3.desc,
    },
    {
      icon: Star,
      title: t.benefits.item4.title,
      desc: t.benefits.item4.desc,
    },
  ];

  return (
    <section
      id="benefits"
      ref={ref}
      className="relative py-24 md:py-40 bg-[#2A2624] overflow-hidden"
      data-testid="benefits-section"
    >
      {/* Watermark Key Logo */}
      <div 
        className="absolute inset-0 flex items-center justify-center pointer-events-none"
        style={{ opacity: 0.03 }}
      >
        <img 
          src={KEY_LOGO_URL} 
          alt="" 
          className="w-[60%] max-w-2xl h-auto object-contain invert"
          aria-hidden="true"
        />
      </div>

      {/* Grain Texture */}
      <div 
        className="absolute inset-0 opacity-[0.06] pointer-events-none"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`,
        }}
      />

      <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={isInView ? 'visible' : 'hidden'}
        >
          {/* Header */}
          <div className="text-center mb-16 md:mb-20">
            <motion.span
              variants={itemVariants}
              className="text-xs tracking-[0.25em] uppercase text-[#B89C6A] font-outfit font-semibold"
            >
              {t.benefits.overline}
            </motion.span>
            
            <motion.h2
              variants={itemVariants}
              className="font-cormorant text-4xl sm:text-5xl lg:text-6xl tracking-tight text-[#FDFBF7] mt-4 mb-6"
            >
              {t.benefits.title}
            </motion.h2>

            <motion.p
              variants={itemVariants}
              className="font-outfit text-base md:text-lg text-[#9A948C] max-w-2xl mx-auto leading-relaxed"
            >
              {t.benefits.subtitle}
            </motion.p>
          </div>

          {/* Benefits Grid */}
          <motion.div
            variants={containerVariants}
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8"
          >
            {benefits.map((benefit, index) => (
              <motion.div
                key={index}
                variants={itemVariants}
                className="group text-center p-8 bg-[#FDFBF7]/5 hover:bg-[#FDFBF7]/10 transition-all duration-500 border border-[#FDFBF7]/10 hover:border-[#B89C6A]/30"
                data-testid={`benefit-${index}`}
              >
                <div className="w-14 h-14 mx-auto mb-5 rounded-full bg-[#B89C6A]/10 group-hover:bg-[#B89C6A]/20 flex items-center justify-center transition-colors duration-500">
                  <benefit.icon 
                    size={28} 
                    weight="light"
                    className="text-[#B89C6A]"
                  />
                </div>
                <h3 className="font-cormorant text-xl text-[#FDFBF7] mb-3">
                  {benefit.title}
                </h3>
                <p className="font-outfit text-sm text-[#9A948C] leading-relaxed">
                  {benefit.desc}
                </p>
              </motion.div>
            ))}
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};
